import { Phone, Mail, MapPin } from "lucide-react";
import { useEffect } from "react";
import { useSEO } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { trackEvent, trackFbq } from "@/lib/analytics";

export default function Reservation() {
  const { t, lang, alternateUrls } = useI18n();
  useSEO({
    title: t("reservation.title"),
    description: lang === "en"
      ? "Book a table at L'Entracte, restaurant in Ville d'Avray. Online booking via Zenchef or by phone at +33 1 82 00 18 67."
      : "Réservez votre table à L'Entracte, restaurant à Ville d'Avray. Réservation en ligne via Zenchef ou par téléphone au 01 82 00 18 67.",
    keywords: lang === "en" ? "book restaurant Ville d'Avray, reservation Zenchef, French restaurant booking" : "réservation restaurant Ville d'Avray, réserver table, Zenchef",
    lang,
    alternates: alternateUrls(),
  });

  // 1) Fire a "reservation page reached" conversion-friendly event as soon as the page is opened.
  useEffect(() => {
    // GA4 — an intent event we can mark as a Conversion in GA4 UI.
    trackEvent("view_reservation_page", {
      page_path: "/reservation",
      currency: "EUR",
      value: 0,
    });
    // Meta — Standard event: ViewContent (visited a page with reservation intent)
    trackFbq("ViewContent", { content_category: "restaurant_reservation", content_name: "Reservation Page" });
  }, []);

  // 2) Listen to postMessage events emitted by the Zenchef iframe.
  // Zenchef sends messages during the booking flow. We forward relevant ones to GA4 + Meta.
  useEffect(() => {
    const handler = (event) => {
      const origin = event.origin || "";
      if (!/(zenchef\.com|sdk\.zenchef\.com|bookings\.zenchef\.com)/.test(origin)) return;
      const data = event.data;
      if (!data) return;

      // Normalise into a string type
      const type = (typeof data === "object" && (data.type || data.event || data.name)) || (typeof data === "string" ? data : "");
      const step = (typeof data === "object" && (data.step || data.state)) || "";
      const eventKey = String(type || step || "").toLowerCase();

      if (!eventKey) return;

      // GA4 — always forward with a namespaced event
      trackEvent("zenchef_event", {
        zenchef_type: String(type || ""),
        zenchef_step: String(step || ""),
        source: "reservation_iframe",
      });

      // Recognise a successful booking (Zenchef fires "confirmation" / "booking_success" / "booked").
      const isSuccess = /(confirm|booked|success|completed|thank[-_ ]?you)/i.test(eventKey);
      if (isSuccess) {
        // Number of guests + price if available
        const guests = (typeof data === "object" && (data.guests || data.covers || data.persons)) || undefined;
        const value = (typeof data === "object" && (data.total || data.amount || data.price)) || 0;

        // GA4 conversion — mark "reservation_confirmed" as a Conversion in GA4 UI
        trackEvent("reservation_confirmed", {
          currency: "EUR",
          value,
          guests,
          source: "zenchef",
        });
        // Meta — "Schedule" standard event with a value we can optimise on
        trackFbq("Schedule", {
          content_category: "restaurant_reservation_completed",
          currency: "EUR",
          value: value || 0,
          num_guests: guests,
        });
        // Meta — high-intent standard event
        trackFbq("CompleteRegistration", {
          content_name: "Restaurant reservation",
          status: "completed",
        });
      }

      // Step tracking (started / date-picked / etc.)
      if (/(step|date|time|slot|guest)/i.test(eventKey)) {
        trackFbq("InitiateCheckout", { content_category: "restaurant_reservation", step: eventKey });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const zenchefSrc = lang === "en"
    ? "https://bookings.zenchef.com/results?rid=367589&lang=en"
    : "https://bookings.zenchef.com/results?rid=367589";

  return (
    <div data-testid="reservation-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto">
        <p className="overline">{t("reservation.overline")}</p>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">{t("reservation.title")}</h1>
        <p className="mt-6 text-[#697585] text-base md:text-lg max-w-2xl">
          {t("reservation.sub")}
        </p>
      </section>

      <section className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 pb-28">
        <div className="md:col-span-8">
          <div data-testid="zenchef-embed-wrapper" className="bg-white border border-[#D9D3C5] overflow-hidden">
            <iframe
              data-testid="zenchef-iframe"
              title="Réservation L'Entracte"
              src={zenchefSrc}
              className="w-full"
              style={{ height: "780px", border: "0", display: "block" }}
              allow="payment"
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-[#697585] mt-4">
            {t("reservation.privacyNote")}
          </p>
        </div>

        <aside className="md:col-span-4 space-y-8">
          <div className="border border-[#D9D3C5] bg-white p-8">
            <p className="overline">{t("reservation.byPhone")}</p>
            <a href="tel:+33182001867" className="mt-3 block font-display text-3xl text-[#203240] hover:text-[#B8956A] transition-colors">
              01 82 00 18 67
            </a>
            <p className="text-sm text-[#697585] mt-3">{t("reservation.hours")}</p>
          </div>

          <div className="border border-[#D9D3C5] bg-white p-8 space-y-4">
            <p className="overline">{t("reservation.info")}</p>
            <div className="flex items-start gap-3 text-sm">
              <MapPin size={16} className="text-[#B8956A] mt-1" />
              <span className="text-[#203240]">3 rue de Versailles<br/>92410 Ville d'Avray</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Phone size={16} className="text-[#B8956A] mt-1" />
              <span className="text-[#203240]">01 82 00 18 67</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Mail size={16} className="text-[#B8956A] mt-1" />
              <span className="text-[#203240]">restaurantlentractevda@gmail.com</span>
            </div>
          </div>

          <div className="border border-[#D9D3C5] bg-white p-8">
            <p className="overline">{t("reservation.hoursTitle")}</p>
            <ul className="mt-4 space-y-2 text-sm text-[#203240]">
              {t("reservation.hoursList").map(([day, hours]) => (
                <li key={day} className="flex justify-between"><span>{day}</span><span className="text-[#697585]">{hours}</span></li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
